import SoftButton from "components/SoftButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftTypography from "components/SoftTypography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { Checkbox, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftBox";
import { useState, useEffect } from "react";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import { ThemedXIcon } from "assets/CXFIcons/Static";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useResourceDependencies } from "hooks/useResourceDependencies";

const DeleteComponentConfirmationDialog = ({
  open,
  onClose,
  onAccept,
  component
}) => {
  const [deleteFlow, setDeleteFlow] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { flows, handleFetchFlows, areFlowsLoaded, isLoadingPortalFlows } =
    usePortalFlows(false);
  const {
    dependencies: connectorFlowIds,
    isLoading: isLoadingDependencies,
    hasFetched: hasFetchedDependencies,
    error: dependenciesError
  } = useResourceDependencies({
    sourceId: component?.id,
    direction: "reverse",
    targetResourceType: "flow",
    shouldFetch: open && Boolean(component?.id)
  });

  useEffect(() => {
    if (open) {
      setDeleteFlow(false);
      setIsDeleting(false);
      handleFetchFlows();
    }
  }, [open]);

  const flowsUsingTheConnector = connectorFlowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId))
    .filter(Boolean);
  const isLoadingFlowNames = connectorFlowIds.length > 0 && (!areFlowsLoaded || isLoadingPortalFlows);
  const isCheckingDependencies =
    open && component?.id && (!hasFetchedDependencies || isLoadingDependencies);
  const isCheckingUsage = isCheckingDependencies || isLoadingFlowNames;
  const hasCheckFailed = Boolean(dependenciesError);
  const isConnectorUsed = flowsUsingTheConnector.length > 0;

  const handleAccept = async () => {
    if (isDeleting || isCheckingUsage || hasCheckFailed) return;

    setIsDeleting(true);
    const shouldClose = await onAccept();

    if (shouldClose !== false) {
      onClose();
    }

    setIsDeleting(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="sm">
        <DialogTitle
          sx={{
            marginBottom: "20px",
            padding: "32px 24px 0 24px !important",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          className="white-to-dark"
          data-testid="form-title"
        >
          <SoftBox display="flex" alignItems="center">
            <ThemedTrashIcon sx={{ fontSize: "16px !important", marginRight: "16px" }} />
            Trash
          </SoftBox>
          <IconButton onClick={onClose}>
            <ThemedXIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0 32px 24px",
          }}
        >
          <SoftBox className="border-top" sx={{ paddingTop: "20px" }}>
            {isCheckingUsage && (
              <SoftBox
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <CircularProgress size={20} />
                <SoftTypography color="text" sx={{ fontSize: "14px" }}>
                  Checking flows using this connector...
                </SoftTypography>
              </SoftBox>
            )}

            {!isCheckingUsage && isConnectorUsed && (
              <SoftBox
                sx={{
                  alignItems: "center",
                  backgroundColor: "#FFEDED",
                  border: "1px solid #E53E3E",
                  display: "flex",
                  borderRadius: "9px",
                  color: "#E53E3E",
                  minHeight: "59px",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <Icon sx={{ fontSize: "16px !important", marginRight: "16px" }}>warning</Icon>
                <SoftTypography
                  sx={{
                    color: "#E53E3E",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Warning: This connector is actively being used
                </SoftTypography>
              </SoftBox>
            )}

            {!isCheckingUsage && hasCheckFailed && (
              <SoftBox
                sx={{
                  alignItems: "center",
                  backgroundColor: "#FFEDED",
                  border: "1px solid #E53E3E",
                  display: "flex",
                  borderRadius: "9px",
                  color: "#E53E3E",
                  minHeight: "59px",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <Icon sx={{ fontSize: "16px !important", marginRight: "16px" }}>warning</Icon>
                <SoftTypography
                  sx={{
                    color: "#E53E3E",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Usage could not be verified. Please try again before deleting.
                </SoftTypography>
              </SoftBox>
            )}

            {!isCheckingUsage && isConnectorUsed && flowsUsingTheConnector.length > 0 && (
              <>
                <SoftTypography
                  className="white-to-dark"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  Flows using this connector:
                </SoftTypography>
                <SoftBox
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    border: "1px solid #E6E6E6",
                    borderRadius: "8px",
                    padding: "8px",
                    marginBottom: "16px",
                  }}
                >
                  {flowsUsingTheConnector.map((flow) => (
                    <SoftTypography key={flow.id} sx={{ fontSize: "14px", marginBottom: "4px" }}>
                      {flow.name}
                    </SoftTypography>
                  ))}
                </SoftBox>
              </>
            )}

            {!isCheckingUsage && !hasCheckFailed && !isConnectorUsed && (
              <>
                <SoftTypography
                  className="white-to-dark"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  Delete Connector
                </SoftTypography>
                <SoftTypography
                  color="text"
                  sx={{
                    fontSize: "14px",
                    marginBottom: "16px",
                  }}
                >
                  Deleting forever will permanently delete this connector and it cannot be retrieved.
                </SoftTypography>
                <SoftBox
                  sx={{
                    alignItems: "flex-start",
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "24px",
                  }}
                >
                  <Checkbox
                    id="confirmDelete"
                    style={{
                      marginRight: "20px",
                    }}
                    checked={deleteFlow}
                    onChange={(event) => {
                      setDeleteFlow(event.target.checked);
                    }}
                  />
                  <SoftTypography
                    component="label"
                    htmlFor="confirmDelete"
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    I’m sure I want to delete this connector.
                  </SoftTypography>
                </SoftBox>
                <SoftBox>
                  <SoftButton
                    sx={{ backgroundColor: "#E53E3E !important", width: "100%" }}
                    color="error"
                    onClick={handleAccept}
                    disabled={!deleteFlow || isDeleting}
                  >
                    {isDeleting ? (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: "#fff",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px",
                          marginLeft: "-12px",
                          zIndex: 1,
                        }}
                      />
                    ) : (
                      "delete forever"
                    )}
                  </SoftButton>
                </SoftBox>
              </>
            )}
          </SoftBox>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteComponentConfirmationDialog;
